import { AnzahlField } from '@/domain/mitarbeiter/features/feiertagskonto/features/feiertagskonto/AnzahlField';
import { UebertragAusVorjahrSchema } from '@/domain/mitarbeiter/features/feiertagskonto/features/feiertagskonto/UebertragAusVorjahrSchema';
import { useUpdateUebertragAusVorjahr } from '@/domain/mitarbeiter/features/feiertagskonto/hooks/useUpdateUebertragAusVorjahr/useUpdateUebertragAusVorjahr';
import { UpdateFeiertagskontoUebertragAusVorjahrInputProps } from '@/domain/mitarbeiter/features/feiertagskonto/types/UebertragAusVorjahrInput';
import { useIsMobile } from '@/hooks';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  toast,
} from '@e2n/ui/v2';
import { faPen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export function UpdateUebertragAusVorjahr({
  jahr,
  mitarbeiterId,
}: UpdateFeiertagskontoUebertragAusVorjahrInputProps) {
  const { t } = useTranslation();
  const [updateUebertragsAusVorjahr] = useUpdateUebertragAusVorjahr();
  const [open, setOpen] = useState(false);
  const [anzahl, setAnzahl] = useState<string | number>(0);
  const [error, setError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const formattedJahr = dayjs(jahr).year();
  const { schema } = UebertragAusVorjahrSchema();
  const isMobile = useIsMobile();

  function validateAnzahl(value: number | string): { data: number } {
    const validAnzahl = schema.safeParse(value);

    if (!validAnzahl.success) {
      setError(
        t('domain.mitarbeiter.feiertagskonto.uebertragAusVorjahr.error'),
      );
      return;
    }
    const validatedAnzahl = validAnzahl.data;
    return { data: validatedAnzahl };
  }

  async function handleUpdate() {
    if (isSubmitting) return;
    const validAnzahl = schema.safeParse(anzahl);

    if (!validAnzahl.success) {
      setError(
        t('domain.mitarbeiter.feiertagskonto.uebertragAusVorjahr.error'),
      );
      return;
    }
    setIsSubmitting(true);
    setError(undefined);
    const validatedAnzahl = validateAnzahl(anzahl).data;
    try {
      const result = await updateUebertragsAusVorjahr({
        input: {
          mitarbeiterId: mitarbeiterId,
          jahr: formattedJahr,
          uebertragAusVorjahr: validatedAnzahl,
        },
      });
      setOpen(false);

      if (result.error) {
        toast({
          variant: 'error',
          description: t(
            'domain.mitarbeiter.feiertagskonto.uebertragAusVorjahr.resultError',
          ),
        });
      } else {
        toast({
          variant: 'success',
          description: t(
            'domain.mitarbeiter.feiertagskonto.uebertragAusVorjahr.resultSuccess',
          ),
        });
      }
      setIsSubmitting(false);
      setAnzahl(0);
    } catch (_error) {
      toast({
        variant: 'error',
        description: t(
          'domain.mitarbeiter.feiertagskonto.uebertragAusVorjahr.resultError',
        ),
      });
      setIsSubmitting(false);
      setAnzahl(0);
    }
  }
  return isMobile ? (
    <Drawer
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
        setAnzahl(0);
      }}>
      <DrawerTrigger asChild>
        <Button iconOnly size="medium" variant="secondary-red">
          <FontAwesomeIcon icon={faPen} className="size-4" />
        </Button>
      </DrawerTrigger>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>
            {t('domain.mitarbeiter.feiertagskonto.uebertragAusVorjahr.title')}
          </DrawerTitle>
          <AnzahlField
            error={error}
            anzahl={anzahl}
            setError={setError}
            setAnzahl={setAnzahl}
            validateAnzahl={validateAnzahl}
          />
          <DrawerFooter>
            <Button
              variant="primary-pill"
              type="submit"
              onClick={handleUpdate}
              disabled={isSubmitting}>
              {isSubmitting ? t('actions.loading') : t('actions.fertig')}
            </Button>
            <DrawerClose asChild>
              <Button
                variant="secondary-blue"
                onClick={() => {
                  setAnzahl(0);
                  setError(undefined);
                  setIsSubmitting(false);
                }}>
                {t('actions.abbrechen')}
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerHeader>
      </DrawerContent>
    </Drawer>
  ) : (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
        setAnzahl(0);
      }}>
      <DialogTrigger asChild>
        <Button iconOnly size="medium" variant="secondary-red">
          <FontAwesomeIcon icon={faPen} className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('domain.mitarbeiter.feiertagskonto.uebertragAusVorjahr.title')}
          </DialogTitle>
          <AnzahlField
            error={error}
            anzahl={anzahl}
            setError={setError}
            setAnzahl={setAnzahl}
            validateAnzahl={validateAnzahl}
          />
        </DialogHeader>
        <DialogFooter variant="default">
          <DialogClose asChild>
            <Button
              variant="secondary-blue"
              onClick={() => {
                setAnzahl(0);
                setError(undefined);
                setIsSubmitting(false);
              }}>
              {t('actions.abbrechen')}
            </Button>
          </DialogClose>
          <Button
            variant="primary-pill"
            type="submit"
            onClick={handleUpdate}
            disabled={isSubmitting}>
            {isSubmitting ? t('actions.loading') : t('actions.fertig')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
